import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Box from '@mui/material/Box'
import clsx from 'clsx'
import Grid from '@mui/material/Grid'
import { Theme, alpha } from '@mui/material/styles'
import Autocomplete, { AutocompleteRenderGroupParams, autocompleteClasses } from '@mui/material/Autocomplete'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import Popper, { PopperProps } from '@mui/material/Popper'
import Paper, { PaperProps } from '@mui/material/Paper'
import { Loader } from '@carsys/ui'
import Collapse from '@mui/material/Collapse'
import SearchDropdown from './components/SearchDropdown'
import { ResultObjectType } from '@cbgms/api'
import { inputBaseClasses } from '@mui/material/InputBase'
import { inputLabelClasses } from '@mui/material/InputLabel'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import CancelIcon from '@mui/icons-material/Cancel'
import { IconButton } from '@mui/material'
import { TyreIcon } from '@carsys/ui/src/icons/TyreIcon'
import { useDebouncedCallback } from 'use-debounce'
import { Timing } from '@cbgms/base/utils/timing'
import { KeyType, isKeyType, newEnterKeyDownEvent } from '@cbgms/base/utils/keyevent'
import { ITopSearchResultProps, ResultComponentSelector } from './Results/ResultComponentSelector'
import { SilentErrorIcon } from '../SilentErrorIcon'
import { useTopSearchContext } from '../context/TopSearchContext'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      [`& .${inputBaseClasses.root}`]: {
        paddingBottom: 0,
        paddingTop: 0,
        paddingRight: '0 !important'
      },
      [`& .${inputLabelClasses.outlined}`]: {
        color: theme.palette.primaryThemeColor.contrastText,
        width: 365,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: 0
      }
    },
    searchLoader: {
      paddingLeft: '24px',
      display: 'flex',
      alignContent: 'center',
      '&>*': {
        display: 'flex'
      }
    },
    popper: {
      width: 'unset !important'
    },
    rootAuto: {
      position: 'relative',
      backgroundColor: theme.palette.background.default,
      marginLeft: 0,
      [`& .${autocompleteClasses.inputRoot} .${autocompleteClasses.input}`]: {
        paddingTop: `${theme.spacing(1)} !important`,
        paddingBottom: `${theme.spacing(1)} !important`
      }
    },
    listbox: {
      width: 600,
      maxHeight: '80vh',
      [`& .${autocompleteClasses.groupUl} .${autocompleteClasses.option}`]: {
        paddingLeft: 0,
        cursor: 'default'
      },
      padding: 0,
      backgroundColor: theme.palette.divider,
      [`& .${autocompleteClasses.option}`]: {
        padding: '2px 0px'
      }
    },
    textColor: {
      color: theme.palette.text.secondary,
      '&::placeholder, &::-webkit-input-placeholder, &::-moz-placeholder, &:-ms-input-placeholder, &:-moz-placeholder': {
        textOverflow: 'ellipsis !important'
      }
    },
    searchRoot: {
      background: theme.palette.background.default,
      padding: 15
    },
    optionRoot: {
      [`&.${autocompleteClasses.option}.${autocompleteClasses.focused}`]: {
        backgroundColor: alpha(theme.palette.primary.main, 0.6)
      }
    },
    groupHeader: {
      padding: theme.spacing(1),
      marginBottom: -4,
      backgroundColor: theme.palette.LKQGrey.background.surface,
      fontWeight: 'bold',
      color: theme.palette.LKQGrey.background.highlight
    },
    groupUL: {
      padding: 0,
      '&.tyres-group': {
        background: alpha(theme.palette.common.white, 0.8),
        '& .searchRoot': {
          paddingTop: theme.spacing(0.5),
          paddingBottom: theme.spacing(0.5),
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1)
        }
      }
    }
  })
)

const ListboxComponentNew = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { children } = props

  return (
    <div ref={ref}>
      <Collapse unmountOnExit mountOnEnter timeout='auto' in appear>
        <List {...props}>{children}</List>
      </Collapse>
    </div>
  )
})

function PopperComponent(props: PopperProps) {
  return <Popper {...props} placement='bottom-start' />
}

type IPaperProps = PaperProps<React.ElementType, { loading: boolean }>

function PaperComponent({ loading, children, ...props }: IPaperProps) {
  return (
    <Paper {...props} sx={{ borderTopLeftRadius: 0 }}>
      {children}
    </Paper>
  )
}

function GroupHeader(params: AutocompleteRenderGroupParams) {
  const classes = useStyles()
  const { t } = useTranslation()
  // we style tyres-group differently, we can only see the group key, that is translated string
  const isTyres = t(`Enum.EnumResultObjectType_${ResultObjectType.TyreSize}`) === params.group

  return (
    <li {...params}>
      {params.group && (
        <Divider textAlign='left' className={classes.groupHeader}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isTyres && (
              <Box sx={{ display: 'flex', mr: 1 }}>
                <TyreIcon />
              </Box>
            )}
            {params.group}
          </Box>
        </Divider>
      )}
      <ul className={clsx(classes.groupUL, { 'tyres-group': !!isTyres })}>{params.children}</ul>
    </li>
  )
}

const Icon: React.FC<{ loading: boolean }> = ({ loading }) => {
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(loading)
  const debouncedSetIsLoading = useDebouncedCallback(value => setIsLoading(value), Timing.ExtralongWait, { maxWait: Timing.ExtralongWait })

  useEffect(() => {
    if (loading) {
      debouncedSetIsLoading(true)
    } else {
      setIsLoading(false)
      debouncedSetIsLoading?.cancel()
    }
  }, [loading, setIsLoading, debouncedSetIsLoading])

  return isLoading ? (
    <Loader loading fading={false} className={clsx(classes.textColor, classes.searchLoader)} size={24} />
  ) : (
    <SearchIcon className={classes.textColor} />
  )
}

const optionLabelGetter = (option: string | ITopSearchResultProps | null) => {
  if (!option || typeof option === 'string') {
    return ''
  } else if (option.Vehicle) {
    return option.Vehicle.Values[0].Manufacturer
  } else if (option.WorkorderInvoice && option.WorkorderInvoice.InvoiceNumber) {
    return option.WorkorderInvoice.InvoiceNumber
  } else if (option.WorkorderQuotation && option.WorkorderQuotation.QuotationNumber) {
    return option.WorkorderQuotation.QuotationNumber
  } else if (option.VehicleSalesQuotation && option.VehicleSalesQuotation.QuotationNumber) {
    return option.VehicleSalesQuotation.QuotationNumber
  } else if (option.Order && option.Order.ID) {
    return option.Order.ID
  } else if (option.Account) {
    return `${option.Account.FirstName} ${option.Account.Name}`
  } else {
    return ''
  }
}

// useBufferedEnter will capture an 'Enter' keyboard event to handle it differently
// this is usefull when we have to wait for the api to return results while also allowing users press enter immediatly
// prevents the event from happening until the loaded bool turns true
// when other keyboard events happen in the mean time, it will swallow the event entirely
//
// returns
// - a ref to be passed to the input,
//  - onKeyDown function
const useBufferedEnter = (loading: boolean) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [shouldEnterWhenLoaded, setShouldEnterWhenLoaded] = useState(false)
  const [isSkippingBuffer, setIsSkippingBuffer] = useState(false)
  const bufferEnter = useCallback(
    (e: React.KeyboardEvent) => {
      // buffered enter: wait for load to complete and re-trigger enter after that
      e.stopPropagation()
      e.preventDefault()
      setShouldEnterWhenLoaded(true)
    },
    [setShouldEnterWhenLoaded]
  )
  const reset = useCallback(() => {
    // only call setState when its actually needed, otherwise it seems to lag the keyboard
    if (isSkippingBuffer) {
      setIsSkippingBuffer(false)
    }
    if (shouldEnterWhenLoaded) {
      setShouldEnterWhenLoaded(false)
    }
  }, [setShouldEnterWhenLoaded, setIsSkippingBuffer, shouldEnterWhenLoaded, isSkippingBuffer])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isKeyType(e, KeyType.ModifierKey)) {
        // ignore shift/contorl/alt keystrokes
        return
      }
      if (isKeyType(e, KeyType.EnterKey)) {
        if (loading && !isSkippingBuffer) {
          bufferEnter(e)
        } else {
          // non-buffered enter: allow event to bubble, reset state
          reset()
        }
        return
      }
      if (isKeyType(e, KeyType.ArrowDownKey) || isKeyType(e, KeyType.ArrowUpKey)) {
        // user selected a search suggestions. stop buffering and allow immediate selection
        setIsSkippingBuffer(true)
        return
      }
      // any other key was pressed:
      if (shouldEnterWhenLoaded || isSkippingBuffer) {
        // reset whenever a non-enter key is pressed
        reset()
      }
    },
    [setIsSkippingBuffer, isSkippingBuffer, shouldEnterWhenLoaded, reset, loading]
  )

  useEffect(() => {
    if (shouldEnterWhenLoaded && !loading && !!inputRef.current) {
      reset()
      // enter was pressed while loading, now we're done loading, trigger the enter
      inputRef.current.dispatchEvent(newEnterKeyDownEvent())
    }
  }, [loading, reset, shouldEnterWhenLoaded, inputRef])

  return useMemo(() => ({ inputRef, onKeyDown, reset }), [inputRef, onKeyDown, reset])
}

export const TopSearch: React.FC<React.PropsWithChildren<{}>> = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const {
    searchPlaceholder,
    noResultsPlaceholder,
    loading,
    results,
    clearQuery,
    setQuery,
    query,
    silentErrors,
    showSearchFilter,
    recentlyOpenedEntities,
    refetchRecentlyOpenedEntities,
    onClose,
    open,
    setOpen
  } = useTopSearchContext()

  const onOpen = useCallback(() => {
    refetchRecentlyOpenedEntities()
    setOpen(true)
  }, [setOpen])

  // determine if we should open the search based on business logic
  // collapse when loading
  // never open for in productSearchingMode. Product search results are only shown in the product list. The search is executed on Enter
  const { searchOptions, controlledOpen } = React.useMemo(() => {
    if (loading && results.length === 0) return { searchOptions: [], controlledOpen: false }
    if (!query.length) return { searchOptions: recentlyOpenedEntities, controlledOpen: open && !!recentlyOpenedEntities?.length }
    return { searchOptions: results, controlledOpen: open }
  }, [loading, query, results, open])
  const borderStyle = controlledOpen ? { borderRadius: '4px 4px 0px 0px' } : { borderRadius: 1 }

  const { onKeyDown, inputRef, reset: resetBufferedEnter } = useBufferedEnter(loading)

  const handleOnchange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      setQuery(e.currentTarget?.value || '')
    },
    [setQuery]
  )

  const onSeachItemSelect = useCallback(
    (_: React.SyntheticEvent, option: string | ITopSearchResultProps | null) => {
      if (!option || typeof option === 'string') {
        return
      }
      option?.primaryAction?.()
      resetBufferedEnter()
    },
    [resetBufferedEnter]
  )

  const startAdornment = useMemo(
    () => (
      <InputAdornment position='start' style={{ marginLeft: 8 }}>
        <Icon loading={loading} />
      </InputAdornment>
    ),
    [loading]
  )

  const endAdornment = useMemo(
    () => (
      <InputAdornment position='end'>
        {query && (
          <IconButton onClick={clearQuery} data-testid='clear-search-button'>
            <CancelIcon />
          </IconButton>
        )}
        {controlledOpen && <SilentErrorIcon silentErrors={silentErrors} />}
        {showSearchFilter && <SearchDropdown />}
      </InputAdornment>
    ),
    [clearQuery, query, controlledOpen, silentErrors, showSearchFilter]
  )

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexShrink: 1, flexBasis: 'auto', height: '100%', maxWidth: '450px', width: '100%' }}>
      <Autocomplete
        clearOnEscape
        id='search-autocomplete'
        data-testid='top-search-autocomplete'
        inputValue={query}
        className={classes.rootAuto}
        sx={borderStyle}
        fullWidth
        classes={{
          popper: classes.popper,
          listbox: classes.listbox
        }}
        PopperComponent={PopperComponent as any /* mui types inconsistant, fixed in mui-v6 */}
        PaperComponent={PaperComponent as React.ComponentType<React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>>}
        componentsProps={{ paper: { loading } as IPaperProps }}
        ListboxComponent={ListboxComponentNew as React.ComponentType<React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>>}
        open={controlledOpen}
        freeSolo
        selectOnFocus
        autoHighlight
        onChange={onSeachItemSelect}
        onOpen={onOpen}
        onClose={onClose}
        filterOptions={options => options}
        groupBy={option => (option.Title ? t(option.Title) : '')}
        renderGroup={params => <GroupHeader {...params} />}
        getOptionLabel={optionLabelGetter}
        noOptionsText={t(noResultsPlaceholder)}
        options={searchOptions}
        renderOption={(props, option) => (
          <li {...props} key={props.id} className={clsx(classes.optionRoot, props.className)}>
            <Grid container className={clsx(classes.searchRoot, 'searchRoot')}>
              <ResultComponentSelector {...option} />
            </Grid>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            ref={inputRef}
            data-testid='top-menu-search-input'
            placeholder={t(searchPlaceholder)}
            variant='outlined'
            className={classes.root}
            onChange={handleOnchange}
            onKeyDown={onKeyDown}
            InputProps={{
              ...params.InputProps,
              type: 'search',
              startAdornment,
              endAdornment,
              classes: {
                input: classes.textColor
              }
            }}
          />
        )}
      />
    </Box>
  )
}
